import { Injectable } from '@angular/core';
import { mergeMap, map, catchError } from 'rxjs/operators';
import { of, forkJoin } from 'rxjs';
import { Effect, Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { AppState } from '../../reducers';
import {
  PharmacyActionTypes,
  PharmaciesPageRequested,
  PharmaciesPageLoaded,
  PharmacyCreated,
  PharmacyDeleted,
  PharmacyUpdated,
  PharmacyOnServerCreated,
  PharmaciesActionToggleLoading,
  PharmaciesPageToggleLoading,
  ThrowPharmacyError,
  FirePharmacySuccess,
  PharmacyOnServerUpdated,
  PharmaciesPageCancelled,
  PharmacyOnServerDeleted,
} from '../_actions/pharmacy.actions';
import { Router } from '@angular/router';
import { PharmacyService } from '../_services';
import { AuthService } from '../../auth/_services';

@Injectable()
export class PharmacyEffects {
  showPageLoadingDistpatcher = new PharmaciesPageToggleLoading({ isLoading: true });
  hidePageLoadingDistpatcher = new PharmaciesPageToggleLoading({ isLoading: false });
  showActionLoadingDistpatcher = new PharmaciesActionToggleLoading({ isLoading: true });
  hideActionLoadingDistpatcher = new PharmaciesActionToggleLoading({ isLoading: false });

  @Effect()
  loadPharmaciesPage$ = this.actions$.pipe(
    ofType<PharmaciesPageRequested>(PharmacyActionTypes.PharmaciesPageRequested),
    mergeMap(({ payload }) => {
      this.store.dispatch(this.showPageLoadingDistpatcher);
      const requestToServer = this.pharmacy.findPharmacies(payload.page);
      const lastQuery = of(payload.page);
      return forkJoin([requestToServer, lastQuery]).pipe(
        catchError((error) => {
          return of([error]);
        }),
        map((response) => {
          if (response[0] && response[0].error) {
            if (response[0].status === 401) {
              return response[0];
            } else if (response[0].status === 403) {
              this.router.navigateByUrl('/error/403');
              return new PharmaciesPageCancelled();
            } else {
              this.store.dispatch(new ThrowPharmacyError({ error: response[0].error.error }));
              return new PharmaciesPageCancelled();
            }
          } else {
            const result = response[0];
            return new PharmaciesPageLoaded({
              pharmacies: result.data,
              totalCount: result.count,
              page: response[1],
            });
          }
        }),
      );
    }),
  );

  @Effect()
  deletePharmacy$ = this.actions$.pipe(
    ofType<PharmacyOnServerDeleted>(PharmacyActionTypes.PharmacyOnServerDeleted),
    mergeMap(({ payload }) => {
      this.store.dispatch(this.showActionLoadingDistpatcher);
      return this.pharmacy.deletePharmacy(payload.id).pipe(
        catchError((error) => {
          return of({ error });
        }),
        map((res) => {
          if (res && res.error) {
            return new ThrowPharmacyError({ error: res.error });
          } else {
            this.store.dispatch(this.hideActionLoadingDistpatcher);
            this.store.dispatch(
              new FirePharmacySuccess({ action_type: PharmacyActionTypes.PharmacyDeleted }),
            );
            return new PharmacyDeleted(payload);
          }
        }),
      );
    }),
  );

  @Effect()
  updatePharmacy$ = this.actions$.pipe(
    ofType<PharmacyOnServerUpdated>(PharmacyActionTypes.PharmacyOnServerUpdated),
    mergeMap(({ payload }) => {
      this.store.dispatch(this.showActionLoadingDistpatcher);
      return this.pharmacy.updatePharmacy(payload.pharmacy).pipe(
        catchError((error) => {
          return of({ error });
        }),
        map((res) => {
          if (res && res.error) {
            return new ThrowPharmacyError({ error: res.error });
          } else {
            this.store.dispatch(this.hideActionLoadingDistpatcher);
            this.store.dispatch(
              new FirePharmacySuccess({ action_type: PharmacyActionTypes.PharmacyUpdated }),
            );
            return new PharmacyUpdated(payload);
          }
        }),
      );
    }),
  );

  @Effect()
  createPharmacy$ = this.actions$.pipe(
    ofType<PharmacyOnServerCreated>(PharmacyActionTypes.PharmacyOnServerCreated),
    mergeMap(({ payload }) => {
      this.store.dispatch(this.showActionLoadingDistpatcher);
      return this.pharmacy.createPharmacy(payload.pharmacy).pipe(
        catchError((error) => {
          return of(error);
        }),
        map((res) => {
          if (res && res.error) {
            return new ThrowPharmacyError({ error: res.error });
          } else {
            this.store.dispatch(
              new FirePharmacySuccess({ action_type: PharmacyActionTypes.PharmacyCreated }),
            );
            this.store.dispatch(this.hideActionLoadingDistpatcher);
            return new PharmacyCreated({ pharmacy: res });
          }
        }),
      );
    }),
  );

  constructor(
    private actions$: Actions,
    private pharmacy: PharmacyService,
    private store: Store<AppState>,
    private router: Router,
    private authService: AuthService,
  ) {}
}
